import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import {
  Mic,
  Sliders,
  Download,
  Layers,
  Headphones,
  Wand2,
  ChevronRight,
  Play,
  X,
  Guitar,
  Zap,
  Crown,
  Music,
  AudioWaveform,
  Store,
  Menu,
} from 'lucide-react';

// Custom generated hero images
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/80d70cfb-45f5-4150-9232-fd29d3aaf318/images/00a06d5ceefca6fcacf29d79b0da25e7455973424be6c9e18407526e7cb96146.png";
const GUITAR_GLOW = "https://static.prod-images.emergentagent.com/jobs/80d70cfb-45f5-4150-9232-fd29d3aaf318/images/49cb5e37bab3aa9287bfe0db96425b9f2a5d023f15f4a61661c5d392d5107d41.png";

const features = [
  {
    icon: Layers,
    title: "Multi-Track Recording",
    description: "Up to unlimited tracks with professional precision. Layer guitars, vocals, drums, and synths.",
    color: "from-cyan-500 to-teal-500",
  },
  {
    icon: Guitar,
    title: "Guitar Amp Suite",
    description: "4 amp models, 9 pedal effects, cabinet simulation. Real-time guitar processing.",
    color: "from-amber-500 to-orange-500",
  },
  {
    icon: Sliders,
    title: "Pro Mixer",
    description: "Volume, pan, mute, solo, EQ - complete control over every track with visual feedback.",
    color: "from-purple-500 to-pink-500",
  },
  {
    icon: Music,
    title: "MIDI Instruments",
    description: "Built-in synth and drum pads. Piano roll editor for precise note control.",
    color: "from-indigo-500 to-violet-500",
  },
  {
    icon: Store,
    title: "Marketplace",
    description: "Browse, buy, and sell guitar presets. Monetize your sound design skills.",
    color: "from-emerald-500 to-green-500",
  },
  {
    icon: Download,
    title: "Export & Share",
    description: "Export to WAV or MP3. Cloud sync across all your devices.",
    color: "from-rose-500 to-red-500",
  },
];

const pricingTiers = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    features: ["4 tracks", "2 GB storage", "WAV export", "2 amp models"],
    color: "from-zinc-600 to-zinc-700",
    popular: false,
  },
  {
    name: "Pro",
    price: "$3.99",
    period: "/month",
    features: ["8 tracks", "10 GB storage", "MP3 + WAV", "All 4 amps", "MIDI tools"],
    color: "from-indigo-500 to-purple-500",
    popular: true,
  },
  {
    name: "Studio",
    price: "$8.99",
    period: "/month",
    features: ["Unlimited tracks", "50 GB storage", "Sell on marketplace", "Priority support"],
    color: "from-amber-500 to-orange-500",
    popular: false,
  },
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Demo walkthrough steps
  const demoSteps = [
    {
      title: "Create Your Project",
      description: "Start with up to 8 tracks. Name your project, set the BPM, and begin recording immediately.",
      icon: Layers,
      color: "from-cyan-500 to-blue-500",
    },
    {
      title: "Record & Import Audio",
      description: "Record directly from your microphone or import existing audio files. Support for WAV, MP3, and more.",
      icon: Mic,
      color: "from-purple-500 to-pink-500",
    },
    {
      title: "Guitar Amp & Effects",
      description: "4 amp models with cabinet simulation. Full pedalboard with distortion, delay, reverb, and more.",
      icon: Guitar,
      color: "from-amber-500 to-orange-500",
    },
    {
      title: "Mix Like a Pro",
      description: "Full mixer with volume faders, pan controls, mute/solo, and per-track EQ.",
      icon: Sliders,
      color: "from-emerald-500 to-teal-500",
    },
    {
      title: "Export & Sell",
      description: "Export your mix as WAV or MP3. Sell your presets on the marketplace!",
      icon: Download,
      color: "from-rose-500 to-red-500",
    },
  ];

  return (
    <div className={`min-h-screen overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#030303] text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div 
          className={`absolute top-0 left-1/4 w-[800px] h-[800px] rounded-full blur-[150px] ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-500/5'}`}
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />
        <div 
          className={`absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full blur-[150px] ${isDark ? 'bg-amber-500/10' : 'bg-amber-500/5'}`}
          style={{ transform: `translateY(${-scrollY * 0.05}px)` }}
        />
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-black/60 border-white/5' : 'bg-white/80 border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/string-sauce-logo.png" 
              alt="String Sauce" 
              className="h-10 object-contain"
            />
          </div>
          
          <div className="hidden md:flex items-center gap-1">
            <Button
              variant="ghost"
              onClick={() => navigate('/products')}
              className={`px-4 ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="nav-products-btn"
            >
              Products
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/pricing')}
              className={`px-4 ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="nav-pricing-btn"
            >
              Pricing
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/marketplace')}
              className={`px-4 ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="nav-marketplace-btn"
            >
              Marketplace
            </Button>
            <div className={`w-px h-5 mx-2 ${isDark ? 'bg-zinc-700' : 'bg-gray-300'}`} />
            <ThemeToggle className={isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} />
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className={`px-4 ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="nav-login-btn"
            >
              Log in
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black font-semibold rounded-full px-6 ml-2"
              data-testid="nav-signup-btn"
            >
              Start Free
            </Button>
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden border-t ${isDark ? 'bg-black/95 border-white/10' : 'bg-white/95 border-gray-200'}`}
            >
              <div className="px-6 py-4 space-y-2">
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/products'); setMobileMenuOpen(false); }}
                  className={`w-full justify-start text-orange-400 font-medium`}
                >
                  Products
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                  className={`w-full justify-start ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  DAW Studio
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}
                  className={`w-full justify-start ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Pricing
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/marketplace'); setMobileMenuOpen(false); }}
                  className={`w-full justify-start ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Marketplace
                </Button>
                <div className={`h-px my-2 ${isDark ? 'bg-zinc-800' : 'bg-gray-200'}`} />
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  className={`w-full justify-start ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Log in
                </Button>
                <Button
                  onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black font-semibold"
                >
                  Start Free
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col pt-20 overflow-hidden">
        {/* Hero Background */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_BG}
            alt="String Sauce Studio"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030303] via-transparent to-[#030303]/50" />
        </div>
        
        {/* Centered Logo at Top */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 flex justify-center pt-8 pb-4"
        >
          <img 
            src="/string-sauce-hero-logo.png" 
            alt="String Sauce" 
            className="w-full max-w-sm md:max-w-md lg:max-w-lg object-contain px-4"
            style={{
              filter: `
                drop-shadow(0 0 2px white)
                drop-shadow(0 0 4px white)
                drop-shadow(0 0 6px rgba(255,255,255,0.8))
                drop-shadow(0 0 20px rgba(249,115,22,0.6))
                drop-shadow(0 0 40px rgba(249,115,22,0.4))
                drop-shadow(0 0 60px rgba(249,115,22,0.3))
              `
            }}
          />
        </motion.div>

        {/* Two Column Layout - Headline + Guitar */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-2 gap-12 items-center flex-1">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full mb-6">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              <span className="text-sm text-zinc-400">Mobile DAW with Live Marketplace</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[0.95] mb-6">
              <span className="block text-white">Your Phone.</span>
              <span className="block bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent">
                Your Studio.
              </span>
            </h1>

            <p className="text-xl text-zinc-400 max-w-lg mb-8 leading-relaxed">
              A professional-grade DAW in your pocket. Record, mix, and master tracks 
              with guitar amp simulation, MIDI instruments, and a thriving preset marketplace.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <Button
                onClick={() => navigate('/register')}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black font-bold rounded-full px-8 py-6 text-lg"
                data-testid="hero-cta-btn"
              >
                Start Creating Free
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => setShowDemoModal(true)}
                variant="outline"
                size="lg"
                className="border-orange-500/30 hover:bg-orange-500/10 rounded-full px-8 py-6 text-lg"
                data-testid="demo-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                See Instructions
              </Button>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-6">
              <div className="flex -space-x-3">
                {[1,2,3,4,5].map(i => (
                  <div 
                    key={i} 
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 border-2 border-[#030303] flex items-center justify-center"
                  >
                    <span className="text-xs">{['🎸', '🎹', '🥁', '🎤', '🎧'][i-1]}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-white font-semibold">Join 10,000+ creators</p>
                <p className="text-sm text-zinc-500">Making music on the go</p>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Guitar Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:block relative"
          >
            <div className="relative">
              <img
                src={GUITAR_GLOW}
                alt="String Sauce Guitar"
                className="w-full max-w-md mx-auto"
                style={{ filter: 'grayscale(100%) sepia(80%) saturate(250%) hue-rotate(-5deg) brightness(1.35) contrast(1.1)' }}
              />
              {/* Glow effect - matched to String Sauce orange */}
              <div className="absolute inset-0 bg-[#f97316]/20 blur-[80px] rounded-full" />
            </div>

            {/* Floating Stats Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute top-10 right-0 bg-black/60 backdrop-blur-xl border border-white/10 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Guitar className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">13</p>
                  <p className="text-xs text-zinc-500">Amp Models</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="absolute bottom-20 left-0 bg-black/60 backdrop-blur-xl border border-white/10 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <AudioWaveform className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">20</p>
                  <p className="text-xs text-zinc-500">Pedal Effects</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2 text-zinc-500">
            <span className="text-xs uppercase tracking-widest">Scroll</span>
            <div className="w-px h-12 bg-gradient-to-b from-zinc-500 to-transparent" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="relative py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="text-orange-400 font-semibold tracking-wider uppercase text-sm">Features</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
              Everything You Need to 
              <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent"> Create</span>
            </h2>
            <p className="text-xl text-zinc-500 max-w-2xl mx-auto">
              Professional tools designed for mobile-first music production
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                onMouseEnter={() => setHoveredFeature(idx)}
                onMouseLeave={() => setHoveredFeature(null)}
                className={`relative group p-6 rounded-2xl border transition-all duration-300 ${
                  hoveredFeature === idx 
                    ? 'bg-white/5 border-white/20 scale-[1.02]' 
                    : 'bg-white/[0.02] border-white/5'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} p-3 mb-4 transition-transform duration-300 ${
                  hoveredFeature === idx ? 'scale-110' : ''
                }`}>
                  <feature.icon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-zinc-500 leading-relaxed">{feature.description}</p>

                {/* Hover glow */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 blur-xl transition-opacity duration-300`} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-transparent via-cyan-950/10 to-transparent">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-amber-400 font-semibold tracking-wider uppercase text-sm">Pricing</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
              Start Free, 
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent"> Upgrade Anytime</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {pricingTiers.map((tier, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className={`relative p-6 rounded-2xl border ${
                  tier.popular 
                    ? 'bg-white/5 border-indigo-500/30 scale-105' 
                    : 'bg-white/[0.02] border-white/5'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-indigo-500 text-white text-xs font-bold rounded-full">
                      POPULAR
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold">{tier.name}</h3>
                  <div className="flex items-baseline justify-center mt-2">
                    <span className="text-4xl font-bold">{tier.price}</span>
                    <span className="text-zinc-500 ml-1">{tier.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                      <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${tier.color}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate(tier.name === 'Free' ? '/register' : '/pricing')}
                  className={`w-full ${
                    tier.popular 
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400' 
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {tier.name === 'Free' ? 'Get Started' : 'View Plans'}
                </Button>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-zinc-500 mt-8">
            All plans include access to the community preset browser.{' '}
            <button 
              onClick={() => navigate('/pricing')} 
              className="text-cyan-400 hover:underline"
            >
              Compare all features →
            </button>
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative p-12 rounded-3xl bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/20"
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIi8+PC9zdmc+')] opacity-50" />
            
            <div className="relative">
              {/* CTA Logo */}
              <div className="flex items-center justify-center mb-6">
                <img 
                  src="/string-sauce-logo.png" 
                  alt="String Sauce" 
                  className="h-16 object-contain drop-shadow-[0_0_20px_rgba(249,115,22,0.5)]"
                />
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Make Music?
              </h2>
              <p className="text-xl text-zinc-400 mb-8 max-w-xl mx-auto">
                Join thousands of musicians creating professional tracks on their phones. 
                Start free, no credit card required.
              </p>
              
              <Button
                onClick={() => navigate('/register')}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black font-bold rounded-full px-10 py-6 text-lg"
              >
                Create Your First Track
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center">
              <img src="/string-sauce-logo.png" alt="String Sauce" className="h-8 object-contain" />
            </div>
            
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <button onClick={() => navigate('/products')} className="hover:text-white transition-colors">
                Products
              </button>
              <button onClick={() => navigate('/pricing')} className="hover:text-white transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors">
                Marketplace
              </button>
              <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">
                Login
              </button>
            </div>
            
            <p className="text-sm text-zinc-600">
              © 2026 String Sauce. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      <AnimatePresence>
        {showDemoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
            onClick={() => setShowDemoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-2xl w-full bg-zinc-900 rounded-3xl border border-zinc-800 overflow-hidden"
            >
              {/* Close button */}
              <button
                onClick={() => setShowDemoModal(false)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              {/* Demo content */}
              <div className="p-8">
                {/* Progress */}
                <div className="flex gap-2 mb-8">
                  {demoSteps.map((_, idx) => (
                    <div
                      key={idx}
                      className={`flex-1 h-1 rounded-full transition-colors ${
                        idx <= demoStep ? 'bg-cyan-500' : 'bg-zinc-700'
                      }`}
                    />
                  ))}
                </div>
                
                {/* Current step */}
                <div className="text-center">
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${demoSteps[demoStep].color} p-4`}>
                    {(() => {
                      const Icon = demoSteps[demoStep].icon;
                      return <Icon className="w-full h-full text-white" />;
                    })()}
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-4">{demoSteps[demoStep].title}</h3>
                  <p className="text-zinc-400 text-lg mb-8 max-w-md mx-auto">
                    {demoSteps[demoStep].description}
                  </p>
                  
                  {/* Navigation */}
                  <div className="flex justify-center gap-4">
                    {demoStep > 0 && (
                      <Button
                        variant="outline"
                        onClick={() => setDemoStep(prev => prev - 1)}
                        className="border-zinc-700"
                      >
                        Previous
                      </Button>
                    )}
                    {demoStep < demoSteps.length - 1 ? (
                      <Button
                        onClick={() => setDemoStep(prev => prev + 1)}
                        className="bg-cyan-500 hover:bg-cyan-400"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          setShowDemoModal(false);
                          navigate('/register');
                        }}
                        className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400"
                      >
                        Get Started
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
